import React, { useEffect, useState } from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderTag from "@/enum/OrderTag";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTagWrap from "../OrderTagWrap";
import { isPddConsolidateShippingOrder } from "@/utils/OrderTagUtils";
import ShopType from "@/enum/ShopType";
import OrderStatus from "@/enum/OrderStatus";
import { Tooltip } from "antd";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import { isBlacklistOrder } from "@/utils/OrderUtils";
import { useCompanySettingStore } from "@/store/hooks";

function getPresentTagTipInfo(order: OrderDTO, shop: ShopDTO) {
    if (!shop) return null;

    if (shop.type === ShopType.DOUDIAN) {
        const text1 =
            "1.为保障用户信息及隐私，涉及送礼订单，请勿对收礼人擅自透露订单的支付优惠等信息，请勿对送礼人擅自透露订单的收货地址、物流轨迹等信息";
        const text2 = "2.该订单为送礼订单，发票问题请联系送礼人，发货及售后问题请联系收礼人";

        if (order.status === OrderStatus.PENDING_PAYMENT) {
            return {
                info: (
                    <div>
                        <p>{text1}</p>
                    </div>
                ),
                width: 950,
            };
        } else if ([OrderStatus.PENDING_SHIPMENT, OrderStatus.SHIPPED, OrderStatus.SUCCESS].includes(order.status)) {
            return {
                info: (
                    <div>
                        <p>{text1}</p>
                        <p>{text2}</p>
                    </div>
                ),
                width: 950,
            };
        }
    } else if (shop.type === ShopType.TAOBAO) {
        const text1 = "1.为保障用户信息及隐私，请勿对收货人（收货人nick）擅自透露订单的支付账号、优惠权益等支付信息";
        const text2 = "2.订单的付款人和收货人不同，发货及售后问题请联系收货人；发票问题请联系付款人";
        const text3 = "3.为保障用户信息及隐私，请勿对付款人（付款人nick）擅自透露订单的收货地址、物流轨迹等信息";

        if (order.status === OrderStatus.PENDING_PAYMENT) {
            return {
                info: (
                    <div>
                        <p>{text1}</p>
                    </div>
                ),
                width: 680,
            };
        } else if ([OrderStatus.PENDING_SHIPMENT, OrderStatus.SHIPPED, OrderStatus.SUCCESS].includes(order.status)) {
            return {
                info: (
                    <div>
                        <p>{text1}</p>
                        <p>{text2}</p>
                        <p>{text3}</p>
                    </div>
                ),
                width: 680,
            };
        }
    }

    return null;
}

/**
 * 通用标签组件
 * 包含所有通用标签逻辑
 */
export default function useCommonTagIcons(order: OrderDTO, orderTagSet: Set<OrderTag>, shop: ShopDTO) {
    const companySettingStore = useCompanySettingStore();
    const [showBlacklistTag, setShowBlacklistTag] = useState(false);

    useEffect(() => {
        if (isBlacklistOrder(order)) {
            (async () => {
                const show = await companySettingStore.ensureHintBlacklistDetail(order);

                setShowBlacklistTag(show);
            })();
        } else {
            setShowBlacklistTag(false);
        }
    }, [order.tags]);

    const icons = [];

    if (orderTagSet.has(OrderTag.EXPEDITE_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.EXPEDITE_DELIVERY}
                orderTag={OrderTag.EXPEDITE_DELIVERY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.PRESALE)) {
        icons.push(
            <OrderTagWrap className="order-icon icon-unimportant" key={OrderTag.PRESALE} orderTag={OrderTag.PRESALE} />,
        );
    }

    if (orderTagSet.has(OrderTag.FREE_SF)) {
        icons.push(
            <OrderTagWrap className="order-icon icon-unimportant" key={OrderTag.FREE_SF} orderTag={OrderTag.FREE_SF} />,
        );
    }

    if (orderTagSet.has(OrderTag.SELF_CONTAINED)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                key={OrderTag.SELF_CONTAINED}
                orderTag={OrderTag.SELF_CONTAINED}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.CONSOLIDATED_SHIPPING) && !isPddConsolidateShippingOrder(order)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                key={OrderTag.CONSOLIDATED_SHIPPING}
                orderTag={OrderTag.CONSOLIDATED_SHIPPING}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.PRESENT)) {
        const tipInfo = getPresentTagTipInfo(order, shop);

        const orderTagNode = (
            <OrderTagWrap className="order-icon icon-important" key={OrderTag.PRESENT} orderTag={OrderTag.PRESENT} />
        );

        if (tipInfo) {
            icons.push(
                <Tooltip key={OrderTag.PRESENT} title={tipInfo.info} styles={{ body: { width: tipInfo.width } }}>
                    {orderTagNode}
                </Tooltip>,
            );
        } else {
            icons.push(orderTagNode);
        }
    }

    if (order.abnormalCause) {
        icons.push(
            <Tooltip title={order.abnormalCause} key="abnormalCause">
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.ABNORMAL_ORDER}>
                    异常
                </OrderTagWrap>
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.FULL_PAYMENT_PRESALE)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.FULL_PAYMENT_PRESALE}
                orderTag={OrderTag.FULL_PAYMENT_PRESALE}
            />,
        );
    }

    if (!!(order?.consignee?._streetDoubleAddressKeywords || order.consignee._localStreetDoubleAddressKeywords)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-local-double-address"
                key={OrderTag.LOCAL_DOUBLE_ADDRESS}
                orderTag={OrderTag.LOCAL_DOUBLE_ADDRESS}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SHIPPING_DELAY)) {
        if (shop?.type !== ShopType.DOUDIAN) {
            if (order.shippingDeadline) {
                icons.push(
                    <Tooltip title={`${order.shippingDeadline}前发货`} key={OrderTag.SHIPPING_DELAY}>
                        <OrderTagWrap className="order-icon icon-unimportant" orderTag={OrderTag.SHIPPING_DELAY} />
                    </Tooltip>,
                );
            } else {
                icons.push(
                    <OrderTagWrap
                        className="order-icon icon-unimportant"
                        key={OrderTag.SHIPPING_DELAY}
                        orderTag={OrderTag.SHIPPING_DELAY}
                    />,
                );
            }
        }
    }

    if (orderTagSet.has(OrderTag.ORDER_ITEM_PART_QUANTITY_CANCELED)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.ORDER_ITEM_PART_QUANTITY_CANCELED}
                style={{ whiteSpace: "nowrap" }}
                orderTag={OrderTag.ORDER_ITEM_PART_QUANTITY_CANCELED}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.ORDER_ITEM_PART_QUANTITY_SHIPPED)) {
        icons.push(
            <Tooltip
                key={OrderTag.ORDER_ITEM_PART_QUANTITY_SHIPPED}
                title={
                    <span>
                        订单某种商品只发货了部分数量，没有全部发完，请注意打印发货不要多发货
                        <a href="https://qw15p3s8hv.feishu.cn/wiki/wikcnJ0QjDntm4S6s41uNpBmRFg" target="_blank">
                            点击可查看教程
                        </a>
                    </span>
                }
            >
                <OrderTagWrap
                    className="order-icon icon-important"
                    style={{ whiteSpace: "nowrap" }}
                    orderTag={OrderTag.ORDER_ITEM_PART_QUANTITY_SHIPPED}
                />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.PERIOD_PURCHASE)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                orderTag={OrderTag.PERIOD_PURCHASE}
                key={OrderTag.PERIOD_PURCHASE}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SUBSIDY_SUPPLY_SALE)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                orderTag={OrderTag.SUBSIDY_SUPPLY_SALE}
                key={OrderTag.SUBSIDY_SUPPLY_SALE}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SUBSIDY_SELF_SALE)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                orderTag={OrderTag.SUBSIDY_SELF_SALE}
                key={OrderTag.SUBSIDY_SELF_SALE}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SAMPLE)) {
        icons.push(
            <OrderTagWrap className="order-icon icon-has-gift" key={OrderTag.SAMPLE} orderTag={OrderTag.SAMPLE} />,
        );
    }

    if (orderTagSet.has(OrderTag.DESIGNATED_LOGISTICS)) {
        icons.push(
            <Tooltip
                key={OrderTag.DESIGNATED_LOGISTICS}
                title={
                    <span>
                        该订单指定物流公司为{LogisticsCompanyNameMap[order.designatedLogisticsCompany]}
                        ，请使用该指定物流公司进行打印发货
                    </span>
                }
            >
                <OrderTagWrap
                    className="order-icon icon-important"
                    style={{ whiteSpace: "nowrap" }}
                    orderTag={OrderTag.DESIGNATED_LOGISTICS}
                />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.SUBSIDY_3C)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.SUBSIDY_3C}
                orderTag={OrderTag.SUBSIDY_3C}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SUBSIDY_MAJOR_APPLIANCE)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.SUBSIDY_MAJOR_APPLIANCE}
                orderTag={OrderTag.SUBSIDY_MAJOR_APPLIANCE}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.LH_CUSTOMIZATION)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.LH_CUSTOMIZATION}
                className="order-icon icon-important"
                style={{ whiteSpace: "nowrap" }}
                orderTag={OrderTag.LH_CUSTOMIZATION}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.ORDER_ITEM_PART_BUNDLE_SKU_SHIPPED)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.ORDER_ITEM_PART_BUNDLE_SKU_SHIPPED}
                className="order-icon icon-important"
                orderTag={OrderTag.ORDER_ITEM_PART_BUNDLE_SKU_SHIPPED}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.LOCKED)) {
        icons.push(
            <OrderTagWrap className="order-icon icon-important" key={OrderTag.LOCKED} orderTag={OrderTag.LOCKED} />,
        );
    }

    if (orderTagSet.has(OrderTag.SHIPPED_OR_SUCCESS_ITEM_WITH_AFTERSALE)) {
        icons.push(
            <Tooltip
                key={OrderTag.SHIPPED_OR_SUCCESS_ITEM_WITH_AFTERSALE}
                title={<span>已发货商品项有售后，可到平台商家后台查看售后详情</span>}
            >
                <OrderTagWrap
                    className="order-icon icon-important"
                    style={{ whiteSpace: "nowrap" }}
                    orderTag={OrderTag.SHIPPED_OR_SUCCESS_ITEM_WITH_AFTERSALE}
                />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.OPEN_IN_FESTIVAL)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                key={OrderTag.OPEN_IN_FESTIVAL}
                orderTag={OrderTag.OPEN_IN_FESTIVAL}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.CANNOT_SHIP)) {
        icons.push(
            <Tooltip
                key={OrderTag.CANNOT_SHIP}
                title={
                    <div>
                        该订单包含暂不可发货商品项，如需操作打印发货请查看收货人地址下方的不可发货原因确认无误后再操作
                    </div>
                }
            >
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.CANNOT_SHIP} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.INSTANT_SHIP)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-instant-ship"
                orderTag={OrderTag.INSTANT_SHIP}
                key={OrderTag.INSTANT_SHIP}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SHIP_HOLD)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.SHIP_HOLD}
                className="order-icon icon-important"
                orderTag={OrderTag.SHIP_HOLD}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SHIP_BY_SF)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.SHIP_BY_SF}
                orderTag={OrderTag.SHIP_BY_SF}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SHIP_BY_SF_EXPRESS)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.SHIP_BY_SF_EXPRESS}
                orderTag={OrderTag.SHIP_BY_SF_EXPRESS}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SHIP_BY_SF_EXPRESS_TK)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.SHIP_BY_SF_EXPRESS_TK}
                orderTag={OrderTag.SHIP_BY_SF_EXPRESS_TK}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.QUALITY_INSPECTION)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                key={OrderTag.QUALITY_INSPECTION}
                orderTag={OrderTag.QUALITY_INSPECTION}
            />,
        );
    }

    if (showBlacklistTag) {
        icons.push(
            <OrderTagWrap
                className="order-icon"
                key={OrderTag.BUYER_BLACKLIST}
                orderTag={OrderTag.BUYER_BLACKLIST}
                style={{ background: "black" }}
            />,
        );
    }

    return icons;
}
